import { isChildUser, isParentUser as hasParentRole } from 'src/utils/userRole';
import { isNative, getPlatform } from 'src/utils/platform';
import { env } from 'src/config/env';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import dayjs from 'dayjs';
import { User } from 'src/types/user.types';
import { reportError } from 'src/modules/logs/Sentry';
import { Device } from '@capacitor/device';
import { getModuleExternalId } from 'src/utils/external-id';
import { logFacebookEvent } from 'src/modules/analytics/FacebookSDK';
import { trackMetaPixelEvent } from 'src/modules/analytics/MetaPixel';
import TikTokSdkPlugin from '../../../plugins/TikTokSdkPlugin';
import { isDebugMode } from 'src/contexts/DebugContext';
import {
  logAppsFlyerEvent,
  APPSFLYER_EVENTS,
  APPSFLYER_EVENT_MAP,
} from 'src/modules/AppsFlyer/AppsFlyer.module';
import posthog from 'posthog-js';
import {
  type AnalyticsCaptureOptions,
  capturePosthogEvent,
  clearFamilyGroupPropertiesPushed,
  forgetPreviousIdentityFlags,
  isPosthogAnonymousOnly,
  registerPosthogSuperProps,
  setPosthogFamilyGroup,
  setPosthogPersonProps,
  setPosthogDeviceFlagProps,
  setPosthogPersonPropsForFlags,
  toCleanUserPropertyKey,
  unregisterPosthogSuperProp,
} from 'src/modules/analytics/posthogClient';
import { queryClient } from 'src/queries/base';
import { LocalStorageKey } from 'src/config/constants';
import { EducationLevelCatalogResponse } from 'src/types/educationLevel.types';
import { queryKeys } from 'src/queries/keys';

export type AnalyticsEvents =
  | 'add_child_button_clicked'
  | 'quiz_started'
  | 'quiz_finished'
  | 'quiz_answered'
  | 'quiz_hearts_depleted'
  | 'quiz_chapter_finished'
  | 'lesson_piece_created'
  | 'add_photo_cta_clicked'
  | 'invite_user'
  | 'signup'
  | 'signin'
  | 'onboarding_complete'
  | 'paywall_viewed'
  | 'trial_started'
  | 'trial_ended'
  | 'support_opened'
  | 'purchase'
  | 'quit_quiz'
  | 'challenge_accepted'
  | 'button_click'
  | 'viral_invite_sent'
  | 'referral_code_sent'
  | 'referral_button_clicked'
  | 'referral_page_viewed'
  | 'referral_code_copy_clicked'
  | 'card_review_viewed'
  | 'collectible_card_evolve_clicked'
  | 'collectible_card_evolution_failed'
  | 'collectible_card_rarity_selected'
  | 'star-review-0'
  | 'star-review-1'
  | 'star-review-2'
  | 'star-review-3'
  | 'star-review-4'
  | 'star-review-5'
  | 'button_store_review_clicked'
  | 'store_review_prompt_requested'
  | 'button_negative_review_clicked'
  | 'parent_challenged'
  | 'parent_challenge_shared'
  | 'paywall_ambassador_code_validated'
  | 'paywall_ambassador_code_valid'
  | 'paywall_ambassador_code_invalid'
  | 'paywall_ambassador_code_error'
  | 'ambassador_trial_started'
  | 'lesson_subject_selected'
  | 'lesson_chapter_started'
  | 'lesson_chapter_quit'
  | 'lesson_chapter_finished'
  | 'smartbit_viewed'
  | 'card_pack_opened'
  | 'card_pack_card_revealed'
  | 'shared_lesson_landing_view'
  | 'shared_lesson_cta_click'
  | 'shared_lesson_import'
  | 'anonymous_locked_feature_click'
  | 'anonymous_locked_feature_unlock_click'
  | 'share_family_code'
  | 'family_code_valid'
  | 'family_code_invalid'
  | 'family_code_error'
  | 'lesson_created'
  | 'lesson_evaluation_asked'
  | 'evaluation_date_entered'
  | 'evaluation_date_skipped'
  | 'exam_subject_selected'
  | 'exam_created'
  | 'exam_analysis_completed'
  | 'error_toast_shown'
  | 'push_prompt_viewed'
  | 'onboarding_three_month_goals_viewed'
  | 'onboarding_three_month_goals_submitted'
  | 'onboarding_labor_effect_viewed'
  | 'onboarding_labor_effect_space_opened'
  | 'onboarding_labor_effect_finished'
  | 'push_permission_result'
  | 'att_permission_result'
  | 'shop_floating_button_clicked'
  | 'cards_empty_state_viewed'
  | 'cards_empty_state_shop_clicked'
  | 'reading_aid_enabled'
  | 'vsaq_hint_requested'
  | 'onboarding_role_selected'
  | 'onboarding_free-trial'
  | 'onboarding_reminder'
  | 'paywall_error'
  | 'paywall_result_received'
  | 'paywall_restore_finished'
  | 'revenuecat_identity_resolved'
  | 'funnel_return_page_viewed'
  | 'funnel_return_code_displayed'
  | 'funnel_return_download_clicked'
  | 'funnel_download_app_viewed'
  | 'funnel_download_app_clicked'
  | 'funnel_web_paywall_purchase_started'
  | 'funnel_web_paywall_purchase_completed'
  | 'funnel_landing_viewed'
  | 'funnel_landing_cta_clicked'
  | 'funnel_consent_submitted'
  | 'missions_page_viewed'
  | 'mission_reward_available_shown'
  | 'mission_reward_claimed'
  | 'missions_claim_all_opened'
  | 'missions_claim_all_confirmed'
  | 'referral_code_screen_viewed'
  | 'referral_code_submitted'
  | 'referral_code_valid'
  | 'referral_code_invalid'
  | 'referral_code_skipped'
  | 'multi_photo_page_viewed'
  | 'multi_photo_source_panel_opened'
  | 'multi_photo_source_selected'
  | 'multi_photo_permission_denied'
  | 'multi_photo_upload_failed'
  | 'multi_photo_upload_retry'
  | 'multi_photo_removed'
  | 'multi_photo_edit_opened'
  | 'multi_photo_edit_saved'
  | 'multi_photo_preprocess_error_shown'
  | 'multi_photo_preprocess_error_action'
  | 'multi_photo_tips_opened'
  | 'multi_photo_validate_clicked'
  | 'lesson_generation_error_viewed'
  | 'lesson_generation_error_closed'
  | 'lesson_generation_retry_clicked'
  | 'multi_photo_cancelled'
  | 'multi_exam_photo_page_viewed'
  | 'multi_exam_photo_source_panel_opened'
  | 'multi_exam_photo_source_selected'
  | 'multi_exam_photo_permission_denied'
  | 'multi_exam_photo_upload_failed'
  | 'multi_exam_photo_upload_retry'
  | 'multi_exam_photo_removed'
  | 'multi_exam_photo_edit_opened'
  | 'multi_exam_photo_edit_saved'
  | 'multi_exam_photo_preprocess_error_shown'
  | 'multi_exam_photo_preprocess_error_action'
  | 'multi_exam_photo_tips_opened'
  | 'multi_exam_photo_validate_clicked'
  | 'multi_exam_photo_cancelled'
  | 'exam_piece_created'
  | 'lesson_photos_gallery_viewed'
  | 'lesson_photos_edit_confirmed'
  | 'lesson_photo_zoom_opened'
  | 'lesson_flow_step_viewed'
  | 'lesson_flow_continue_clicked'
  | 'lesson_resource_loading_finished'
  | 'parent_add_lesson_clicked'
  | 'parent_add_lesson_child_selected'
  | 'parent_add_lesson_created_modal_cta_clicked'
  | 'parent_lesson_viewed'
  | 'parent_lesson_tab_selected'
  | 'parent_lesson_knowledge_component_clicked'
  | 'parent_lesson_knowledge_chapter_clicked'
  | 'parent_lesson_mastery_status_clicked'
  | 'parent_lesson_revision_sheet_clicked'
  | 'timetable_upload_started'
  | 'timetable_photo_created'
  | 'timetable_photo_deleted'
  | 'timetable_photo_retry_clicked'
  | 'timetable_photo_viewed'
  | 'timetable_submitted'
  | 'timetable_settings_viewed'
  | 'timetable_updated'
  | 'timetable_picker_later_clicked'
  | 'subjects_updated'
  | 'baohelp_opened'
  | 'baohelp_hook_sent'
  | 'baohelp_suggestion_sent'
  | 'baohelp_message_sent'
  | 'baohelp_response_received'
  | 'baohelp_focus_updated'
  | 'baohelp_focus_opened'
  | 'baohelp_app_link_clicked'
  | 'baohelp_closed'
  | 'baohelp_history_opened'
  | 'baohelp_history_conversation_clicked'
  | 'baohelp_history_new_conversation_clicked'
  | 'baohelp_history_conversation_archive_clicked'
  | 'baohelp_history_conversation_archive_failed'
  | 'baohelp_saved_sheet_viewed'
  | 'block_term_tour_viewed'
  | 'block_term_tour_closed'
  | 'block_passage_term_opened'
  | 'block_intro_viewed'
  | 'block_intro_started'
  | 'block_understood_clicked'
  | 'block_hearts_intro_opened'
  | 'practice_intro_viewed'
  | 'practice_intro_started'
  | 'practice_situation_viewed'
  | 'practice_next_step_clicked'
  | 'practice_next_screen_clicked'
  | 'practice_quit'
  | 'practice_situation_understood_clicked'
  | 'practice_term_opened'
  | 'practice_method_opened'
  | 'practice_answer_history_opened'
  | 'practice_term_closed'
  | 'practice_method_closed'
  | 'practice_answer_history_closed'
  | 'lesson_header_chip_clicked'
  | 'flashcards_intro_started'
  | 'flashcards_tour_started'
  | 'flashcards_tour_completed'
  | 'flashcards_card_flipped'
  | 'flashcards_card_swiped'
  | 'flashcards_undo'
  | 'flashcards_pass_completed'
  | 'flashcards_lets_go_clicked'
  | 'flashcards_restarted'
  | 'flashcards_quit_mid_pass'
  | 'summer_banner_clicked'
  | 'easter_banner_clicked'
  | 'seasonal_spotlight_clicked'
  | 'summer_hub_viewed'
  | 'summer_hub_line_clicked'
  | 'summer_parkour_viewed'
  | 'voice_input_record_started'
  | 'voice_input_recording_completed'
  | 'voice_input_paused'
  | 'voice_input_resumed'
  | 'voice_input_playback_started'
  | 'voice_input_deleted'
  | 'voice_input_submitted'
  | 'voice_input_transcribed'
  | 'voice_input_failed'
  | 'voice_input_permission_denied'
  | 'voice_input_permission_settings_opened'
  | 'voice_input_transcript_cleared'
  | 'camera_session_killed'
  | 'camera_activity_restored'
  | 'camera_kill_redirect'
  | 'camera_kill_photo_restored'
  | 'camera_opened'
  | 'camera_result_restored'
  | 'camera_result_late'
  | 'camera_capture_finished'
  | 'camera_capture_failed'
  | 'free_first_lesson_started'
  | 'progression_path_viewed'
  | 'progression_step_clicked'
  | 'lesson_course_viewed'
  | 'lesson_digest_viewed'
  | 'lesson_digest_section_clicked'
  | 'lesson_digest_print_notify_requested'
  | 'lesson_digest_print_opened'
  | 'lesson_digest_print_closed'
  | 'lesson_digest_closed'
  | 'revision_sheet_clicked'
  | 'progression_step_started'
  | 'progression_challenge_clicked'
  | 'progression_course_clicked'
  | 'streak_page_viewed'
  | 'streak_counter_clicked'
  | 'streak_day_created'
  | 'streak_month_selected'
  | 'streak_shared'
  | 'streak_celebration_viewed'
  | 'streak_celebration_closed'
  | 'celebration_viewed'
  | 'celebration_closed'
  | 'streak_goal_viewed'
  | 'streak_goal_selected'
  | 'streak_goal_failed'
  | 'streak_saved'
  | 'streak_goal_claimed'
  | 'streak_protected_viewed'
  | 'streak_freeze_purchased'
  | 'streak_freeze_starter_granted'
  | 'star_granted'
  | 'star_road_tier_granted'
  | 'star_road_reward_claimed'
  | 'star_road_claim_opened'
  | 'query_error_retry_clicked'
  | 'star_road_help_opened'
  | 'star_road_help_next_clicked'
  | 'star_road_help_back_clicked'
  | 'star_road_help_closed'
  | 'currency_help_closed'
  | 'lesson_card_options_opened'
  | 'lesson_card_option_clicked'
  | 'lesson_card_options_closed'
  | 'parent_lesson_options_opened'
  | 'parent_lesson_option_clicked'
  | 'parent_lesson_options_closed'
  | 'currency_help_opened'
  | 'star_road_opened'
  | 'star_road_finished'
  | 'badge_list_viewed'
  | 'badge_showcase_viewed'
  | 'badge_friend_showcase_viewed'
  | 'badge_detail_opened'
  | 'badge_tier_selected'
  | 'badge_celebration_viewed'
  | 'badge_showcase_updated'
  | 'badge_share_opened'
  | 'badge_share_clicked'
  | 'friends_page_viewed'
  | 'friends_add_friend_clicked'
  | 'friend_request_resolved'
  | 'friend_profile_opened'
  | 'profile_picker_viewed'
  | 'profile_picker_profile_selected'
  | 'profile_picker_add_clicked'
  | 'profile_switch_failed'
  | 'app_update_finished'
  | 'app_update_failed'
  | 'native_update_required_viewed'
  | 'appsflyer_uid_resolved'
  | 'app_boot_recovered'
  | 'app_bundle_recovered'
  | 'app_device_profiled'
  | 'splash_released'
  | 'reload_cover_failed';

export type SubjectsChangeSource =
  | 'parent_child_onboarding'
  | 'child_onboarding'
  | 'child_family_tab_creation'
  | 'edit_profile'
  | 'lesson_wrong_subject_notification';

let isParentUser = true;

export const setIsParentUser = (isParent: boolean) => {
  isParentUser = isParent;
};

export async function logAnalyticsEvent(
  eventName: AnalyticsEvents,
  eventParams?: Record<string, unknown>,
  options?: AnalyticsCaptureOptions,
): Promise<void> {
  if (env.isCi || isDebugMode()) {
    return;
  }

  if (options) capturePosthogEvent(eventName, eventParams, options);
  else capturePosthogEvent(eventName, eventParams);

  if (eventName === 'signup' && isNative()) {
    try {
      await TikTokSdkPlugin.trackEvent({
        eventName: 'Registration',
      });
    } catch (e) {
      reportError('Error sending TikTok event', e);
    }
  }

  if (!isParentUser) return;

  if (eventName === 'signup') {
    trackMetaPixelEvent('CompleteRegistration');
  }
  if (eventName === 'trial_started' && eventParams?.is_paid === true) {
    trackMetaPixelEvent(
      'StartTrial',
      undefined,
      eventParams?.rc_app_user_id
        ? `trial_started_${eventParams.rc_app_user_id}`
        : undefined,
    );
  }
  if (eventName === 'purchase') {
    trackMetaPixelEvent(
      'Purchase',
      eventParams?.af_revenue && eventParams?.af_currency
        ? { value: eventParams.af_revenue, currency: eventParams.af_currency }
        : undefined,
      eventParams?.rc_app_user_id
        ? `purchase_${eventParams.rc_app_user_id}`
        : undefined,
    );
  }

  if (eventName === 'trial_started' && eventParams?.is_paid === true) {
    try {
      await logFacebookEvent('StartTrial');
      await logFacebookEvent('Subscribe');
    } catch (e) {
      reportError('Error sending Facebook SDK event', e);
    }
  }

  if (eventName === 'purchase') {
    try {
      await logFacebookEvent('Subscribe');
    } catch (e) {
      reportError('Error sending Facebook SDK event', e);
    }
  }

  if (eventName === 'signup') {
    try {
      await logFacebookEvent('Signup');
    } catch (e) {
      reportError('Error sending Facebook SDK event', e);
    }
  }

  if (
    APPSFLYER_EVENTS.includes(eventName) &&
    (eventName !== 'trial_started' || eventParams?.is_paid === true)
  ) {
    try {
      const afEventName = APPSFLYER_EVENT_MAP[eventName] ?? eventName;
      await logAppsFlyerEvent(afEventName, eventParams);
    } catch (e) {
      reportError('Error sending AppsFlyer event', e);
    }
  }
}

export const logSubjectsUpdated = (params: {
  subjects: string[];
  source: SubjectsChangeSource;
  childId: number;
  isSelf: boolean;
}) => {
  void logAnalyticsEvent('subjects_updated', {
    subjects: params.subjects,
    subjects_count: params.subjects.length,
    source: params.source,
    child_id: params.childId,
    is_self: params.isSelf,
  });
};

// Skips the batch queue and rides a browser-owned beacon: Android can kill the process seconds
// after the tap or right after backgrounding.
export const BEACON_DELIVERY: AnalyticsCaptureOptions = {
  send_instantly: true,
  transport: 'sendBeacon',
};

export const initAnalytics = async () => {
  if (env.isCi || isDebugMode()) {
    return;
  }

  if (!isNative()) {
    return;
  }

  const currentBuild = await CapacitorUpdater.current();
  registerPosthogSuperProps({
    app_version: currentBuild.bundle.version,
    native_version: currentBuild.native,
  });

  try {
    const info = await Device.getInfo();
    // Rounded to a power of two and clamped by the WebView (4 GB -> 4, 6 GB -> 4); absent on old WebViews.
    const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
    const memoryProps = typeof deviceMemory === 'number' ? { device_memory: deviceMemory } : {};
    registerPosthogSuperProps({
      device_platform: info.platform,
      device_os_version: info.osVersion,
      device_manufacturer: info.manufacturer,
      device_model: info.model,
      device_os_name: info.operatingSystem,
      ...memoryProps,
    });
    // Lets flag release conditions target devices; sent with /flags, not persisted on the person.
    setPosthogDeviceFlagProps({
      device_model: info.model,
      device_os_name: info.operatingSystem,
      device_os_version: info.osVersion,
      ...memoryProps,
    });
    // Device class, once per launch, joined onto early-boot OTA events via `install_id` (a
    // super-property already registered at initPosthog module-scope — posthogClient.ts) rather than
    // by hoisting this bridge call earlier: it is an async round-trip, so hoisting would only
    // narrow the race, not close it, and initPosthog also runs on web (AppWeb.tsx), where stamping
    // device properties would wrongly tag web traffic.
    logAnalyticsEvent('app_device_profiled', {
      deviceModel: info.model,
      deviceOsVersion: info.osVersion,
      devicePlatform: info.platform,
    });
    // Last in the block so a storage failure cannot suppress the events above. Read by the next
    // launch's camera-kill detection, which runs before Device.getInfo() can resolve.
    localStorage.setItem(
      LocalStorageKey.DEVICE_PROFILE,
      JSON.stringify({ model: info.model, osVersion: info.osVersion }),
    );
  } catch (e) {
    console.warn('Error setting device info', e);
  }
};

export const SUPER_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  'user_type',
  'family_id',
  'grade_level',
  'school_group',
  'school_id',
  'country',
  'year_of_birth',
  'gender',
  'education_level_code',
  'education_level_group',
]);

export const getEducationLevelGroupSlug = (
  educationLevelCode: string | null | undefined,
  country: string | null | undefined,
): string | undefined => {
  if (!educationLevelCode) return undefined;

  const catalog = queryClient.getQueryData<EducationLevelCatalogResponse>(
    queryKeys.educationLevels(country),
  );

  return catalog?.groups.find(group =>
    group.levels.some(level => level.code === educationLevelCode),
  )?.code;
};

export const PERSON_ONLY_ATTRIBUTE_KEYS: ReadonlySet<string> = new Set([
  'objectives',
  'user_id',
]);

export const buildUserAttributes = (
  user: User,
): Record<string, string | number | string[] | null | undefined> => {
  const attributes: Record<string, string | number | string[] | null | undefined> = {
    user_type: user.user_type,
    family_id: String(user.family_id),
    grade_level: user.school_level,
    objectives: user.objectives,
    // The RAW id, never getModuleExternalId(): that helper prefixes non-production ids with
    // 'test_', which is right for the distinct_id but would make a flag condition targeting user_id
    // = 12345 match in prod and silently miss in staging QA.
    user_id: String(user.id),
  };

  if (user.date_of_birth) {
    const yearOfBirth = dayjs(user.date_of_birth).year();
    if (!Number.isNaN(yearOfBirth)) {
      attributes.year_of_birth = yearOfBirth;
    }
  }
  // The API returns `country`, `school_id` and `family_member_type` as nullable (`country?: string
  // | null`, `school_id?: number | null`, `family_member_type?: ... | null`); the hand-written
  // `User` type is more optimistic and declares them non-nullable, so tsc can't see it.
  if (user.country != null) {
    attributes.country = user.country;
  }
  if (user.school_id != null) {
    attributes.school_id = user.school_id;
  }
  if (user.education_level_code) {
    attributes.education_level_code = user.education_level_code;

    const groupSlug = getEducationLevelGroupSlug(
      user.education_level_code,
      user.country,
    );
    if (groupSlug) {
      attributes.school_group = groupSlug;
      attributes.education_level_group = groupSlug;
    }
  }
  if (isChildUser(user) && user.family_member_type != null) {
    attributes.gender = user.family_member_type;
  }

  return attributes;
};

const isRegistrableValue = (
  value: unknown,
): value is string | number | string[] =>
  value != null && !Number.isNaN(value);

const superPropertiesFrom = (
  attributes: Record<string, string | number | string[] | null | undefined>,
): Record<string, string | number | string[]> =>
  Object.fromEntries(
    Object.entries(attributes).filter(
      (entry): entry is [string, string | number | string[]] =>
        SUPER_PROPERTY_KEYS.has(entry[0]) && isRegistrableValue(entry[1]),
    ),
  );

export const identifyUser = (user?: User) => {
  if (env.isCi || isDebugMode()) {
    // Analytics stay silent, but flags must still evaluate for the real user: staging-app desktop
    // requires ?debug=true (store redirect) and PR previews build VITE_CI — both would otherwise
    // pin person-prop flags to false.
    if (user) {
      setPosthogPersonPropsForFlags({
        user_type: user.user_type,
        grade_level: user.school_level,
        country: user.country,
      });
    }
    return;
  }

  if (!user) {
    return;
  }

  setIsParentUser(hasParentRole(user));

  const userId = getModuleExternalId(user.id);
  const attributes = buildUserAttributes(user);

  if (posthog.__loaded && !isPosthogAnonymousOnly()) {
    // A new identity must not see the previous one's arms: forget them, then identify (its reload
    // fetches the new answers).
    if (posthog.get_distinct_id() !== userId) forgetPreviousIdentityFlags();
    posthog.identify(userId, attributes);
    const superProps = superPropertiesFrom(attributes);
    // Super-properties are browser-scoped and localStorage-persisted, and register() merges.
    for (const key of SUPER_PROPERTY_KEYS) {
      if (!(key in superProps)) unregisterPosthogSuperProp(key);
    }
    registerPosthogSuperProps(superProps);
    setPosthogFamilyGroup(String(user.family_id));
  }

  setPosthogPersonProps(attributes);

  if (isNative()) {
    TikTokSdkPlugin.setUserProperties({
      userId: String(user.id),
    }).catch((e: unknown) => reportError('Error setting TikTok user properties', e));
  }
};

export const addUserProperty = (
  key: string,
  value?: string | string[] | number,
) => {
  if (env.isCi || isDebugMode()) {
    return;
  }

  // Unsets are deliberately a no-op: PostHog has never received them (the legacy path returned
  // before the PostHog leg), and person properties are effectively irreversible at scale.
  if (!isRegistrableValue(value) || isPosthogAnonymousOnly()) {
    return;
  }

  setPosthogPersonProps({ [key]: value });

  const cleanKey = toCleanUserPropertyKey(key);
  if (SUPER_PROPERTY_KEYS.has(cleanKey)) {
    registerPosthogSuperProps({ [cleanKey]: value });
  }
};

export function trackPaywallError(params: {
  operation:
    | 'purchase'
    | 'fetch_offerings'
    | 'restore_purchases'
    | 'login'
    | 'identity';
  error_code: string;
  error_message?: string;
  placement?: string;
  package_type?: string;
  user_type?: string;
  is_premium?: boolean;
  premium_ends_at?: string;
  family_id?: number;
}) {
  logAnalyticsEvent('paywall_error', {
    ...params,
    platform: getPlatform(),
  });
}

export const resetAnalytics = () => {
  if (env.isCi || isDebugMode()) {
    return;
  }
  if (posthog.__loaded) {
    posthog.reset();
    // posthog.reset() wipes $groups and any pushed group properties: the family-group-properties
    // marker must not outlive it, or a same-family re-login's property-less group-key attach
    // (identifyUser) would satisfy useFlagAssignmentReporter's guard before this new session ever
    // actually pushes properties.
    clearFamilyGroupPropertiesPushed();
  }
};

export const getAnalyticsDistinctId = () => {
  if (env.isCi || isDebugMode()) {
    return undefined;
  }

  return posthog.get_distinct_id();
};
